import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { getData, addData, updateData, deleteData } from '../../services/regaddonsServices';
import { Modal, Button, Form } from 'react-bootstrap';
import DataTable from 'react-data-table-component';
import Header from '../../components/Layout/Header';
import Footer from '../../components/Layout/Footer';
import Sidebar from '../../components/Layout/Sidebar';
import Spinner from '../../components/Spinner';
import { toast } from 'react-toastify';

const Regaddons = () => {
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [dataList, setDataList] = useState([]);
  const [form, setForm] = useState({ id: '', regaddon_name: '', regaddon_price: '', regaddon_euro_price: '' });
  const [modalType, setModalType] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [selectedData, setSelectedData] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  // Fetch data list
  const fetchData = useCallback(async () => {
    setLoading(true); 
    try{
    const data = await getData();
    setDataList(data);
    }
    catch (error) {
      console.error('Error fetching data:', error);
    }
    finally {
    setTimeout(() => {
      setLoading(false);
    }, 500);
  }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

 const validateForm = () => {
    const newErrors = {};
    if (!form.regaddon_name) {
      newErrors.regaddon_name = "Addon Name is required";
    }

  // Validate regaddon_price
  if (!form.regaddon_price) {
    newErrors.regaddon_price = "Addon Price For USD is required";
  } else if (!/^\d+(\.\d{1,3})?$/.test(form.regaddon_price)) {
    newErrors.regaddon_price = "Enter a valid number";
  }

  // Validate regaddon_euro_price
  if (!form.regaddon_euro_price) {
    newErrors.regaddon_euro_price = "Addon Price For EURO is required";
  } else if (!/^\d+(\.\d{1,3})?$/.test(form.regaddon_euro_price)) {
    newErrors.regaddon_euro_price = "Enter a valid number";
  } 
    setErrors((prev) => ({ ...prev, ...newErrors }));
    return Object.keys({ ...errors, ...newErrors }).every((key) => !errors[key] && !newErrors[key]);
  };

  const handleChange = (e) => {
    try{
    const { name, value } = e.target;
    let updatedErrors = { ...errors };
    setForm((prev) => ({ ...prev, [name]: value }));
     if (value) {
        updatedErrors[name] = ''; // Clear error if corrected
      }
      setErrors(updatedErrors);
    }
    catch (error) {
      console.error('Error in handleChange:', error);
    } 
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
      if (!validateForm()) {
        return
      }
    try{
       let result;
    if (modalType === 'add') {
      result =  await addData(form);
       if (result.status === 201) {
        toast.success("Saved Successfully!", {
                      position: 'top-right',
                      autoClose: 3000,
                    });
       }
       else{
         toast.error(result.message ||"Failed to update data. Please try again.", {
                  position: 'top-right',
                  });
       }
    } else if (modalType === 'edit') {
     result = await updateData(form);
     if(result.status === 200){
     toast.success("Updated Successfully!", {
                   position: 'top-right',
                   autoClose: 3000,
                });
    }
    else{
      toast.error(result.message ||"Failed to update data. Please try again.", {
       position: 'top-right'});
      console.log("Failed to process the request.");
    }
    }
    fetchData();  // Re-fetch data after adding/editing
    setShowModal(false);
  }
  catch (error) { 
        console.error("Error:", error.message);
        }
  };

  const handleDelete = async () => {
    try{
    if (selectedData) {
      // Optimistically remove the data from the list
      setDataList((prev) => prev.filter((data) => data.id !== selectedData.id));
      // Proceed with the actual delete request
      await deleteData(selectedData);
      setShowDeleteConfirmModal(false);
      toast.success("Deleted Successfully!", {
                   position: 'top-right',
                   autoClose: 3000,
                });
    }
  }
  catch(error){
    console.log("error during deleting data", error);
  }
  };

  const openModal = (type, data = { id: '', regaddon_name: '', regaddon_price: '', regaddon_euro_price: '' }) => {
    setModalType(type);
    setForm(data);
    setShowModal(true);
  };
  
  const openDeleteConfirmModal = (data) => {
    setSelectedData(data);
    setShowDeleteConfirmModal(true);
  };

  const handleSearchChange = (e) => {
    setSearchText(e.target.value);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  // Memoized filtered data based on search query
  const filteredData = useMemo(() => {
    try{
      const lowerSearch = searchText.toLowerCase();
      const  filtered =  dataList.filter((data) =>
        data.regaddon_name.toLowerCase().includes(lowerSearch) ||
        String(data.regaddon_price).toLowerCase().includes(lowerSearch)||
        String(data.regaddon_euro_price).toLowerCase().includes(lowerSearch)
    );
    const sortedData = [...filtered].sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt));
    return sortedData;
  }
  catch(error){
    console.log("error during filter data",error);
  }
  }, [dataList, searchText]);

  const columns = [
    {
      name: '#',
      selector: (row, index) => (currentPage - 1) * 10 + (index + 1),
      sortable: false,
      width: '80px',
    },
    {
      name: 'Addon Name',
      selector: (row) => row.regaddon_name,
      cell: (row) => (
            <span title={row.regaddon_name}>
              {row.regaddon_name}
            </span>
          ),
      sortable: true,
    },
    {
      name: 'Addon Price For USD',
      selector: (row) => row.regaddon_price,
      sortable: true,
    },
    {
        name: 'Addon Price For EURO',
        selector: (row) => row.regaddon_euro_price,
        sortable: true,
      },
    {
      name: 'Actions',
      cell: (row) => (
        <div>
          <Button className="me-2" variant="warning" size="sm" onClick={() => openModal('edit', row)} > Edit </Button>
          <Button variant="danger" size="sm" onClick={() => openDeleteConfirmModal(row)} > Delete </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="d-flex">
      <Sidebar />
      <div className="main-content w-100">
        <Header />
        {loading ? (
          <div className="d-flex justify-content-center align-items-center w-100" style={{ minHeight: "300px" }}>
          <Spinner />
        </div>
      ) :(
        <div>
        <div className="container-fluid p-4">
          <div className="row">
            <div className="col-8">
              <p className='mb-3 fs-4'>Basic Setup & File upload / Registration Setup <b>/ Registration Addons</b></p>
            </div>
            <div className="col-4 text-end">
              <button className="btn btn-primary mb-3" onClick={() => openModal('add')}>
                Add
              </button>
            </div>
          </div>

          {/* Data Table for data */}
          <DataTable
            columns={columns}
            data={filteredData}
            pagination
            search
            highlightOnHover
            responsive
            striped
            paginationPerPage={10}
            paginationComponentOptions={{
            noRowsPerPage: true,
            }}
            onChangePage={handlePageChange}
            subHeader
            subHeaderComponent={
              <div>
                <input className="form-control" type="text" placeholder="Search" value={searchText}
                  onChange={handleSearchChange}
                />
              </div>
            }
          />

          {/* Modal for Add/Edit data */}
          <Modal show={showModal} onHide={() => setShowModal(false)} backdrop="static">
            <Modal.Header closeButton>
              <Modal.Title className='fs-3'>{modalType === 'add' ? 'Add' : 'Edit'}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <Form onSubmit={handleSubmit}>
                <Form.Group className="mb-3">
                  <Form.Label className='form-label-custom'>Addon Name <small className='text-danger'> * </small></Form.Label>
                  <Form.Control type="text" name="regaddon_name" value={form.regaddon_name} onChange={handleChange}  isInvalid={!!errors.regaddon_name}/>
                  <Form.Control.Feedback type="invalid">{errors.regaddon_name}</Form.Control.Feedback>
                  
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label className='form-label-custom'>Addon Price For USD <small className='text-danger'> * </small></Form.Label>
                  <Form.Control type="text" name="regaddon_price" value={form.regaddon_price} onChange={handleChange} isInvalid={!!errors.regaddon_price}/>
                  <Form.Control.Feedback type="invalid">{errors.regaddon_price}</Form.Control.Feedback>

                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label className='form-label-custom'>Addon Price For EURO <small className='text-danger'> * </small></Form.Label>
                  <Form.Control type="text" name="regaddon_euro_price" value={form.regaddon_euro_price} onChange={handleChange} isInvalid={!!errors.regaddon_euro_price} />
                  <Form.Control.Feedback type="invalid">{errors.regaddon_euro_price}</Form.Control.Feedback>
                </Form.Group>
             
            <Modal.Footer className='border-0'>
              <Button variant="secondary" onClick={() => setShowModal(false)}> Cancel </Button>
              <Button variant="primary" type='submit'> {modalType === 'add' ? 'Add' : 'Save'} </Button>
            </Modal.Footer>
             </Form>
            </Modal.Body>
          </Modal>

          {/* Delete Confirmation Modal */}
          <Modal show={showDeleteConfirmModal} onHide={() => setShowDeleteConfirmModal(false)}>
            <Modal.Header closeButton>
              <Modal.Title>Delete</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <p>Are you sure you want to delete this row?</p>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={() => setShowDeleteConfirmModal(false)}>
                Cancel
              </Button>
              <Button variant="danger" onClick={handleDelete}>
                Yes, Delete
              </Button>
            </Modal.Footer>
          </Modal>
        </div>
        <Footer />
        </div>
        )}
      </div>
    </div>
  );
};

export default Regaddons;